import { useState, useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useOrderAddresses } from '@/hooks/features/orders/useOrderAddresses'
import { Plus, MapPin, Check } from 'lucide-react'
import { AddressDialog } from './AddressDialog'
import { AddressFormState } from './addressFieldsConfig'
import { cn } from '@/lib/utils'
import { Spinner } from '@/components/ui/spinner'

interface AddressSelectDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    userId?: string
    currentAddressId?: string
    onSelect: (addressId: string) => void
}

export const AddressSelectDialog = ({
    open,
    onOpenChange,
    userId,
    currentAddressId,
    onSelect
}: AddressSelectDialogProps) => {
    const { addresses, loading, fetchAddresses, createAddress } = useOrderAddresses(userId)
    const [isAddOpen, setIsAddOpen] = useState(false)

    useEffect(() => {
        if (open && userId) {
            fetchAddresses()
        }
    }, [open, userId, fetchAddresses])

    const handleAddAddress = async (data: AddressFormState) => {
        const newAddress = await createAddress({ ...data, customer_id: userId })
        if (newAddress) {
            setIsAddOpen(false)
        }
    }

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent size="md">
                    <DialogHeader>
                        <DialogTitle>Delivery address</DialogTitle>
                        <DialogDescription>
                            Choose a saved address or add a new one.
                        </DialogDescription>
                    </DialogHeader>

                    {loading && addresses.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-2 py-16">
                            <Spinner className="size-6 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Loading addresses…</p>
                        </div>
                    ) : addresses.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-subdued px-6 py-12 text-center">
                            <MapPin className="size-6 text-muted-foreground" />
                            <p className="text-sm font-medium text-ink">No saved addresses</p>
                            <p className="text-sm text-text-2">Add a new address for this customer.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <p className="text-sm text-ink">Saved addresses</p>
                            {addresses.map((address) => {
                                const selected = currentAddressId === address._id
                                return (
                                    <button
                                        type="button"
                                        key={address._id}
                                        onClick={() => onSelect(address._id)}
                                        aria-pressed={selected}
                                        className={cn(
                                            'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                                            selected
                                                ? 'border-ink bg-selected'
                                                : 'border-border bg-card hover:bg-subdued'
                                        )}
                                    >
                                        <MapPin className="mt-0.5 size-4 shrink-0 text-text-2" />
                                        <div className="min-w-0 flex-1 space-y-0.5">
                                            <p className="truncate text-sm font-medium text-ink">{address.name}</p>
                                            <p className="line-clamp-2 text-sm text-text-2">{address.address}</p>
                                            <p className="text-sm tabular-nums text-text-2">{address.phone}</p>
                                        </div>
                                        {selected && <Check className="size-4 shrink-0 text-ink" />}
                                    </button>
                                )
                            })}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button onClick={() => setIsAddOpen(true)}>
                            <Plus />
                            Add address
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AddressDialog
                open={isAddOpen}
                mode="create"
                editAddress={null}
                closeDialog={() => setIsAddOpen(false)}
                onCreate={handleAddAddress}
                onUpdate={() => { }}
                defaultUserId={userId}
            />
        </>
    )
}
